import { useId, useState } from 'react';
import { Button, DialogShell } from '@timely/ui';
import {
    TEMPLATE_VARIABLES, renderTemplate, type TemplateChannel, type TemplateContext,
} from '@timely/domain';
import { FormNotice } from '../screens/AuthLayout';
import {
    TemplateError, createTemplate, updateTemplate,
    type MessageTemplate, type TemplateDraft,
} from './useTemplates';

const CHANNEL_LABEL: Record<TemplateChannel, string> = {
    whatsapp: 'WhatsApp', email: 'Email', internal: 'Internal note',
};

/** Always available, never a real account — preview never touches real data. */
const SAMPLE_CONTEXT: TemplateContext = {
    customer_name: 'Wilson Brothers',
    outstanding_amount: '£12,400',
    company_name: 'Northwind Traders',
    follow_up_date: '12 Oct',
    expected_amount: '£5,000',
    contact_name: 'Priya',
};

export function TemplateDialog({
    workspaceId, template, onClose, onSaved,
}: {
    workspaceId: string;
    template: MessageTemplate | null;
    onClose: () => void;
    onSaved: (message: string) => void | Promise<void>;
}) {
    const field = useId();
    const [draft, setDraft] = useState<TemplateDraft>({
        name: template?.name ?? '',
        content: template?.content ?? '',
        channel: template?.channel ?? 'whatsapp',
    });
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const insertVariable = (key: string) => {
        setDraft(d => ({ ...d, content: `${d.content}{{${key}}}` }));
    };

    const save = async () => {
        setSaving(true);
        setProblem(null);
        try {
            if (template) {
                await updateTemplate(template.id, draft);
            } else {
                await createTemplate(workspaceId, draft);
            }
            await onSaved(template ? 'Template updated.' : 'Template created.');
        } catch (err) {
            setProblem(err instanceof TemplateError ? err.message : 'That did not work.');
        } finally {
            setSaving(false);
        }
    };

    const preview = renderTemplate(draft.content, SAMPLE_CONTEXT, draft.channel);

    return (
        <DialogShell
            title={template ? 'Edit template' : 'New template'}
            subtitle="Everyone in this company can use it. No other company can see it."
            size="lg"
            onClose={onClose}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button variant="primary" onClick={() => { void save(); }} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </>
            }
        >
            {problem && <div className="mb-4"><FormNotice tone="error">{problem}</FormNotice></div>}

            <label htmlFor={`${field}-name`} className="block text-[12.5px] text-label-3 mb-1">Name</label>
            <input
                id={`${field}-name`}
                type="text" value={draft.name} maxLength={80}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                className="h-11 w-full rounded-lg bg-card-3 px-3 text-[14px] outline-none focus:ring-2 focus:ring-accent mb-4"
            />

            <label htmlFor={`${field}-channel`} className="block text-[12.5px] text-label-3 mb-1">Channel</label>
            <select
                id={`${field}-channel`}
                value={draft.channel}
                onChange={e => setDraft(d => ({ ...d, channel: e.target.value as TemplateChannel }))}
                className="h-11 w-full rounded-lg bg-card-3 px-3 text-[14px] outline-none mb-4"
            >
                {(Object.keys(CHANNEL_LABEL) as TemplateChannel[]).map(c => (
                    <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>
                ))}
            </select>

            <label htmlFor={`${field}-content`} className="block text-[12.5px] text-label-3 mb-1">Content</label>
            <textarea
                id={`${field}-content`}
                value={draft.content} rows={6}
                onChange={e => setDraft(d => ({ ...d, content: e.target.value }))}
                className="w-full rounded-lg bg-card-3 px-3 py-2 text-[14px] outline-none focus:ring-2 focus:ring-accent mb-2"
            />

            <div className="flex flex-wrap gap-1.5 mb-4">
                {TEMPLATE_VARIABLES.map(v => (
                    <button
                        key={v.key} type="button" title={v.description}
                        onClick={() => insertVariable(v.key)}
                        className="text-[11.5px] font-semibold rounded-full bg-accent-tint text-accent px-2.5 py-1"
                    >
                        + {v.label}
                    </button>
                ))}
            </div>

            <label className="block text-[12.5px] text-label-3 mb-1">Preview, with sample data</label>
            <div className="rounded-lg bg-card-3 px-3 py-2 text-[14px] whitespace-pre-wrap min-h-[3em]">
                {preview || <span className="text-label-3">Nothing to show yet.</span>}
            </div>
        </DialogShell>
    );
}
